import { Box, Divider, Typography, styled } from '@mui/material';
import type {
  IAccreditationData,
  IAppDetailsAppData,
  IPlatform,
} from '../common-types';
import {
  mapAccreditationTooltipText,
  platformNameConverter,
} from '../common-utilities';

import type { ImageProps } from 'next/image';
import { AccreditationBadge } from '../ImagesNextJS';

const Container = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(3)} 0`,
}));

export type PlatformAccreditationLogosProps = IPlatform & {
  app: IAppDetailsAppData;
  FlexibleImageComponent: React.ComponentType<ImageProps>;
  translatedText: {
    platformAccreditationsHeading: string;
    accreditationBadgeTooltipText: string;
  };
  configuration?: {
    title?: string;
    hideAccreditations: boolean;
    hideAwards: boolean;
  };
  inLineContent?: boolean;
};

/**
 * PlatformAccreditationsLogos from [MicroLibraryCoreUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryCoreUI) 
 * @param app is required, this is the app data that is passed to the component.
 * @param FlexibleImageComponent is required, this is the component that is used to display the image.
 * @param translatedText is required, this is the text that as already been translated by the consumer of this component.
 * @param configuration is optional, this is the configuration object that can be used to hide the accreditations or awards.  
 *  title?: string;
    hideAccreditations: boolean;
    hideAwards: boolean;
    inLineContent: boolean; // if true, the AccreditationLogos will be displayed in a row
 */

export const PlatformAccreditationLogos = ({
  platformName,
  configuration,
  app,
  translatedText,
  FlexibleImageComponent,
  inLineContent = false,
  ...props
}: PlatformAccreditationLogosProps) => {
  const { platforms } = app ?? {};
  const relevantPlatform = platforms?.find(
    (p) => platformNameConverter(p.platformName) === platformName
  );
  const accreditations = relevantPlatform?.accreditations ?? undefined;
  const awards = relevantPlatform?.awards ?? undefined;
  const showAccreditations =
    configuration === undefined || !configuration?.hideAccreditations;
  const showAwards =
    (configuration === undefined || !configuration?.hideAwards) &&
    awards &&
    awards.length > 0;
  const showDividerAfterAccreditations =
    showAccreditations && accreditations && accreditations.length > 0;

  const defaultLayout = (accreditations: IAccreditationData[]) => (
    <Box sx={{ display: 'grid', gap: (theme) => theme.spacing(2) }}>
      <Typography component="h2" variant="h6">
        {configuration?.title ?? translatedText?.platformAccreditationsHeading}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: (theme) => theme.spacing(2.5),
          flexWrap: 'wrap',
        }}
      >
        {showAccreditations &&
          accreditations.map((accreditation, accreditationIndex) => {
            return (
              <Box
                key={accreditation.id}
                sx={{
                  display: 'flex',
                  gap: (theme) => theme.spacing(2.5),
                }}
              >
                {accreditationIndex > 0 && (
                  <Divider
                    orientation="vertical"
                    sx={{
                      height: '30px',
                      alignSelf: 'center',
                    }}
                    flexItem
                  />
                )}
                {accreditation.logoUrl ? (
                  <AccreditationBadge
                    src={accreditation.logoUrl}
                    FlexibleImageComponent={FlexibleImageComponent}
                    translatedText={{
                      tooltipText: mapAccreditationTooltipText(
                        translatedText?.accreditationBadgeTooltipText,
                        accreditation.name
                      ),
                    }}
                  />
                ) : (
                  <Typography>{accreditation.name}</Typography>
                )}
              </Box>
            );
          })}
      </Box>
      {showAwards && (
        <>
          {showDividerAfterAccreditations && <Divider />}
          <Box
            sx={{
              display: 'flex',
              gap: (theme) => theme.spacing(2.5),
              flexWrap: 'wrap',
            }}
          >
            {awards?.map((award, awardIndex) => {
              return (
                <Box
                  key={award.name}
                  sx={{
                    display: 'flex',
                    gap: (theme) => theme.spacing(2.5),
                  }}
                >
                  {awardIndex > 0 && (
                    <Divider
                      orientation="vertical"
                      sx={{
                        height: '30px',
                        alignSelf: 'center',
                      }}
                      flexItem
                    />
                  )}
                  {award.imageUrl ? (
                    <AccreditationBadge
                      src={award.imageUrl}
                      FlexibleImageComponent={FlexibleImageComponent}
                      translatedText={{
                        tooltipText: mapAccreditationTooltipText(
                          translatedText?.accreditationBadgeTooltipText,
                          award.name
                        ),
                      }}
                    />
                  ) : (
                    <Typography>{award.name}</Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );

  const inlineLayout = (accreditations: IAccreditationData[]) => (
    <Box
      sx={{
        display: 'flex',
        gap: (theme) => theme.spacing(2.5),
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      {showAccreditations &&
        accreditations.map((accreditation) => (
          <Box
            key={accreditation.id}
            sx={{
              display: 'flex',
              gap: (theme) => theme.spacing(2.5),
              alignItems: 'center',
            }}
          >
            {accreditation.logoUrl ? (
              <AccreditationBadge
                src={accreditation.logoUrl}
                FlexibleImageComponent={FlexibleImageComponent}
                translatedText={{
                  tooltipText: mapAccreditationTooltipText(
                    translatedText?.accreditationBadgeTooltipText,
                    accreditation.name
                  ),
                }}
              />
            ) : (
              <Typography>{accreditation.name}</Typography>
            )}
          </Box>
        ))}

      {showAwards && awards && awards.length > 0 && (
        <>
          {awards.map((award) => (
            <Box
              key={award.name}
              sx={{
                display: 'flex',
                gap: (theme) => theme.spacing(2.5),
                alignItems: 'center',
              }}
            >
              {award.imageUrl ? (
                <AccreditationBadge
                  src={award.imageUrl}
                  FlexibleImageComponent={FlexibleImageComponent}
                  translatedText={{
                    tooltipText: mapAccreditationTooltipText(
                      translatedText?.accreditationBadgeTooltipText,
                      award.name
                    ),
                  }}
                />
              ) : (
                <Typography>{award.name}</Typography>
              )}
            </Box>
          ))}
        </>
      )}
    </Box>
  );

  return (
    <>
      {accreditations && accreditations.length > 0 && (
        <Container {...props}>
          {inLineContent
            ? inlineLayout(accreditations)
            : defaultLayout(accreditations)}
        </Container>
      )}
    </>
  );
};
